import { mundelyPlanPrice } from "./pricing";
import { updateNativeWidgetPlan, getMundelyNativeWidgetSettings, saveMundelyNativeWidgetSettings } from "../integrations/nativeWidgets";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { logUserAction } from "../utils/telemetry";
import { useAuth } from "../auth/AuthProvider";
import { completeActive, planCall, type PlanStatus } from "./planService";
import { useI18n } from "../i18n/I18nProvider";

const PlanContext = createContext<{ complete: boolean; status: PlanStatus | null; refresh: () => Promise<void>; error: string }>({ complete: false, status: null, refresh: async () => undefined, error: "" });
export function PlanProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [status, setStatus] = useState<PlanStatus | null>(null), [error, setError] = useState(""), [now, setNow] = useState(Date.now());
  async function refresh() {
    if (!user) { setStatus(null); return; }
    try { setStatus(await planCall()); setError(""); } catch { setError("unavailable"); }
  }
  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    if (!user) return;
    const update = () => {
      setNow(Date.now());
      void planCall().then(next => { if (!cancelled) { setStatus(next); setError(""); } }).catch(() => { if (!cancelled) setError("unavailable"); });
    };
    update();
    const timer = window.setInterval(update, 60000);
    const visible = () => { if (document.visibilityState === "visible") update(); };
    document.addEventListener("visibilitychange", visible);
    return () => { cancelled = true; clearInterval(timer); document.removeEventListener("visibilitychange", visible); };
  }, [user?.uid, profile?.plan, profile?.planExpiresAt]);
  const complete = status ? status.plan === "premium" && (!status.expiresAt || Date.parse(status.expiresAt) > now) : completeActive(profile, now);
  useEffect(() => { void saveMundelyNativeWidgetSettings(getMundelyNativeWidgetSettings()).catch(() => undefined); void updateNativeWidgetPlan(complete, Date.parse(status?.expiresAt || profile?.planExpiresAt || "") || 0).catch(() => undefined); }, [complete, status?.expiresAt, profile?.planExpiresAt]);
  return <PlanContext.Provider value={{ complete, status, refresh, error }}>{children}</PlanContext.Provider>;
}
export function usePlan() { return useContext(PlanContext); }
export function CompleteGate({ children, title, description }: { children: ReactNode; title?: string; description?: string }) {
  const { complete } = usePlan(), { t, language } = useI18n();
  if (complete) return <>{children}</>;
  return <section className="settings-card plan-lock"><strong>{title || t("billing.completeRequired")}</strong><p>{description || t("billing.preserved")}</p><p className="plan-price-hint">{t("billing.upgradePrices",{monthly:mundelyPlanPrice("monthly",language),annual:mundelyPlanPrice("annual",language)})}</p><button className="button primary" onClick={() => { void logUserAction("plan_upgrade_clicked", "billing"); window.dispatchEvent(new Event("mundely:open-plan")); }}>{t("billing.viewPlans")}</button></section>;
}
